import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import ImageDropZone from '../common/ImageDropZone';

/**
 * Brand & vision → the brand library (Jenn, 2026-10-24, migration 186): tag lines,
 * synopses (25 / 50 / 100 words, with a live word count), meta data, hashtags and
 * Team365 people (head-and-shoulders photo, title, bio). Team365 adds its own
 * sections and items; everything has a Copy button.
 */
const box = { width: '100%', fontSize: 16, lineHeight: 1.5, padding: 10, borderRadius: 10, border: '1px solid #C4714A', marginTop: 4, boxSizing: 'border-box', fontFamily: 'inherit' };
const words = (t) => ((t || '').trim().match(/\S+/g) || []).length;

export function CopyButton({ text, label = 'Copy' }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text || ''); }
    catch {
      const t = document.createElement('textarea'); t.value = text || ''; document.body.appendChild(t); t.select();
      try { document.execCommand('copy'); } finally { document.body.removeChild(t); }
    }
    setDone(true); setTimeout(() => setDone(false), 1800);
  };
  return <button type="button" className="btn s" onClick={copy} disabled={!text}>{done ? '✓ Copied' : label}</button>;
}

function WordCount({ text, target }) {
  const n = words(text);
  if (!target) return <span className="qs">{n} words</span>;
  const off = n - target;
  return <span className="qs" style={{ color: Math.abs(off) <= Math.max(2, Math.round(target * 0.1)) ? '#4A6355' : '#C4714A' }}>{n} of {target} words</span>;
}

function ItemForm({ section, item, onDone, onCancel }) {
  const person = section.kind === 'person';
  const [f, setF] = useState({
    label: item?.label || '', body: item?.body || '', personTitle: item?.person_title || '',
    imageUrl: item?.image_url || '', imageAlt: item?.image_alt || '', wordTarget: item?.word_target || '',
  });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const save = async () => {
    setBusy(true); setErr(null);
    try {
      if (item) await api.put(`/admin/sales-marketing/brand-items/${item.id}`, f);
      else await api.post('/admin/sales-marketing/brand-items', { ...f, sectionKey: section.section_key });
      onDone();
    } catch (e) { setErr(e.response?.data?.error || 'Could not save. Please try again.'); setBusy(false); }
  };
  return (
    <div style={{ border: '1px solid #eee5da', borderRadius: 12, padding: 12, marginTop: 10, background: '#fff' }}>
      <label className="qs" style={{ display: 'block' }}>{person ? 'Name' : 'Name of this piece'}
        <input value={f.label} onChange={e => set('label', e.target.value)} style={box} placeholder={person ? 'Example: Patti Dell’Osso' : 'Example: 50-word synopsis'} />
      </label>
      {person && (
        <>
          <label className="qs" style={{ display: 'block', marginTop: 8 }}>Title<input value={f.personTitle} onChange={e => set('personTitle', e.target.value)} style={box} placeholder="Example: Wellness Mentor-at-Large" /></label>
          <div className="qs" style={{ marginTop: 8 }}>Head-and-shoulders photo</div>
          <div style={{ maxWidth: 280, marginTop: 4 }}><ImageDropZone currentUrl={f.imageUrl || null} onUploaded={(u) => set('imageUrl', u)} onClear={() => set('imageUrl', '')} /></div>
          <label className="qs" style={{ display: 'block', marginTop: 8 }}>Describe the photo<input value={f.imageAlt} onChange={e => set('imageAlt', e.target.value)} style={box} /></label>
        </>
      )}
      <label className="qs" style={{ display: 'block', marginTop: 8 }}>{person ? 'Bio' : 'The words'}
        <textarea value={f.body} onChange={e => set('body', e.target.value)} rows={Math.max(3, (f.body.match(/\n/g) || []).length + 3)} style={box} />
      </label>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', marginTop: 4, gap: 8, flexWrap: 'wrap' }}>
        <WordCount text={f.body} target={Number(f.wordTarget) || null} />
        {!person && (
          <label className="qs" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>Word target (optional)
            <input type="number" min="1" max="1000" value={f.wordTarget} onChange={e => set('wordTarget', e.target.value)} style={{ ...box, width: 80, marginTop: 0 }} />
          </label>
        )}
      </div>
      {err && <div className="note">{err}</div>}
      <div className="row" style={{ gap: 8, marginTop: 10 }}>
        <button type="button" className="btn p s" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
        <button type="button" className="btn s" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function Item({ section, item, first, last, onEdit, onChanged }) {
  const person = section.kind === 'person';
  const move = async (dir) => { await api.post(`/admin/sales-marketing/brand-items/${item.id}/move`, { dir }); onChanged(); };
  const remove = async () => {
    if (!window.confirm(`Remove "${item.label || 'this'}"?`)) return;
    await api.post(`/admin/sales-marketing/brand-items/${item.id}/archive`); onChanged();
  };
  const all = person ? [item.label, item.person_title, item.body].filter(Boolean).join('\n') : item.body;
  return (
    <div style={{ borderTop: '1px solid #eee5da', padding: '12px 0', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      {person && (item.image_url
        ? <img src={item.image_url} alt={item.image_alt || item.label || ''} style={{ width: 84, height: 84, objectFit: 'cover', borderRadius: '50%', flexShrink: 0 }} />
        : <div style={{ width: 84, height: 84, borderRadius: '50%', background: '#F0EBE3', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#4A4A4A' }}>No photo</div>)}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <span><b>{item.label || 'Untitled'}</b>{person && item.person_title && <span className="qs"> · {item.person_title}</span>}</span>
          {!person && item.body && <WordCount text={item.body} target={item.word_target} />}
        </div>
        {item.body
          ? <p style={{ whiteSpace: 'pre-wrap', margin: '4px 0 0', lineHeight: 1.6 }}>{item.body}</p>
          : <p className="qs" style={{ margin: '4px 0 0' }}>Not written yet</p>}
        <div className="row" style={{ gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
          <CopyButton text={item.body} label={person ? 'Copy bio' : 'Copy'} />
          {person && <CopyButton text={item.person_title} label="Copy title" />}
          {person && <CopyButton text={all} label="Copy name, title and bio" />}
          {person && item.image_url && <a className="btn s" href={item.image_url} target="_blank" rel="noreferrer" download>Open photo</a>}
          <button type="button" className="btn s" onClick={onEdit}>Edit</button>
          <button type="button" className="btn s" disabled={first} onClick={() => move('up')} aria-label="Move up">↑</button>
          <button type="button" className="btn s" disabled={last} onClick={() => move('down')} aria-label="Move down">↓</button>
          <button type="button" className="btn s" onClick={remove}>Remove</button>
        </div>
      </div>
    </div>
  );
}

function Section({ section, items, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [renaming, setRenaming] = useState(false);
  const [title, setTitle] = useState(section.title);
  const [help, setHelp] = useState(section.help || '');
  const saveName = async () => { await api.put(`/admin/sales-marketing/brand-sections/${section.section_key}`, { title, help }); setRenaming(false); onChanged(); };
  const putAway = async () => {
    if (!window.confirm(`Put away the "${section.title}" section? Its pieces are kept, just hidden.`)) return;
    await api.post(`/admin/sales-marketing/brand-sections/${section.section_key}/archive`); onChanged();
  };
  const allText = items.filter(i => i.body).map(i => (section.kind === 'person' ? [i.label, i.person_title, i.body].filter(Boolean).join('\n') : i.body)).join('\n\n');
  return (
    <div className="card" style={{ marginTop: 14 }}>
      {renaming ? (
        <div>
          <label className="qs">Section name<input value={title} onChange={e => setTitle(e.target.value)} style={box} /></label>
          <label className="qs" style={{ display: 'block', marginTop: 6 }}>Short description<input value={help} onChange={e => setHelp(e.target.value)} style={box} /></label>
          <div className="row" style={{ gap: 8, marginTop: 8 }}>
            <button type="button" className="btn p s" onClick={saveName}>Save</button>
            <button type="button" className="btn s" onClick={() => setRenaming(false)}>Cancel</button>
          </div>
        </div>
      ) : (
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
          <div><p className="q" style={{ margin: 0 }}>{section.title}</p>{section.help && <p className="qs" style={{ margin: '2px 0 0' }}>{section.help}</p>}</div>
          <span className="row" style={{ gap: 6 }}>
            {items.length > 1 && <CopyButton text={allText} label="Copy all" />}
            <button type="button" className="btn s" onClick={() => setRenaming(true)}>Rename</button>
            <button type="button" className="btn s" onClick={putAway}>Put away</button>
          </span>
        </div>
      )}
      {items.length === 0 && !adding && <p className="qs" style={{ marginTop: 8 }}>Nothing here yet.</p>}
      {items.map((it, i) => (editingId === it.id
        ? <ItemForm key={it.id} section={section} item={it} onDone={() => { setEditingId(null); onChanged(); }} onCancel={() => setEditingId(null)} />
        : <Item key={it.id} section={section} item={it} first={i === 0} last={i === items.length - 1} onEdit={() => setEditingId(it.id)} onChanged={onChanged} />))}
      {adding
        ? <ItemForm section={section} onDone={() => { setAdding(false); onChanged(); }} onCancel={() => setAdding(false)} />
        : <button type="button" className="btn s" style={{ marginTop: 10 }} onClick={() => setAdding(true)}>+ Add {section.kind === 'person' ? 'a person' : 'one'}</button>}
    </div>
  );
}

export default function BrandLibrary() {
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState(null);
  const [newSection, setNewSection] = useState(null); // null | { title, kind }
  const load = useCallback(() => {
    api.get('/admin/sales-marketing/brand-library').then(r => setData(r.data)).catch(() => setMsg('Could not load the brand library.'));
  }, []);
  useEffect(() => { load(); }, [load]);
  const addSection = async () => {
    try { await api.post('/admin/sales-marketing/brand-sections', newSection); setNewSection(null); load(); }
    catch (e) { setMsg(e.response?.data?.error || 'Could not add the section.'); }
  };
  return (
    <div style={{ marginTop: 28 }}>
      <h3 className="ptitle" style={{ fontSize: '1.4rem' }}>Ready to copy</h3>
      <p className="pq">Tag lines, synopses, meta data, hashtags and our people — tap Copy on anything and paste it wherever you need it.</p>
      {msg && <div className="note">{msg}</div>}
      {!data ? <p className="pq">Loading…</p> : data.sections.map(s => (
        <Section key={s.section_key} section={s} items={data.items.filter(i => i.section_key === s.section_key)} onChanged={load} />
      ))}
      {newSection ? (
        <div className="card" style={{ marginTop: 14 }}>
          <label className="qs">New section name<input value={newSection.title} onChange={e => setNewSection(x => ({ ...x, title: e.target.value }))} style={box} placeholder="Example: Email sign-offs" /></label>
          <div className="row" style={{ gap: 14, marginTop: 8 }}>
            <label className="qs" style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="radio" checked={newSection.kind === 'text'} onChange={() => setNewSection(x => ({ ...x, kind: 'text' }))} /> Words to copy</label>
            <label className="qs" style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="radio" checked={newSection.kind === 'person'} onChange={() => setNewSection(x => ({ ...x, kind: 'person' }))} /> People (photo, title, bio)</label>
          </div>
          <div className="row" style={{ gap: 8, marginTop: 10 }}>
            <button type="button" className="btn p s" disabled={!newSection.title.trim()} onClick={addSection}>Add section</button>
            <button type="button" className="btn s" onClick={() => setNewSection(null)}>Cancel</button>
          </div>
        </div>
      ) : <button type="button" className="btn s" style={{ marginTop: 14 }} onClick={() => setNewSection({ title: '', kind: 'text' })}>+ Add a section</button>}
    </div>
  );
}
